import { useState, useCallback, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

interface RichTextEditorProps {
  initialValue: string;
  onSave: (richText: string, plainText: string) => Promise<void>;
  placeholder?: string;
  minHeight?: string;
}

/**
 * Rich Text Editor with Manual Save
 *
 * Features:
 * - Rich text editing with formatting toolbar
 * - Manual save button
 * - HTML sanitization for security
 * - Save status indicator
 * - Plain text extraction for search/export
 * - Mobile-optimized toolbar and height
 */
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialValue,
  onSave,
  placeholder = 'Add your notes here...',
  minHeight = '200px'
}) => {
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  const editorRef = useRef<ReactQuill | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Sync local state when the parent pushes a new initialValue (e.g. after a
  // dictation appends transcribed text to the note). Consumers only mutate
  // initialValue after an intentional commit, never mid-typing.
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Track mobile breakpoint
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll editor into view when focused (keyboard avoidance)
  useEffect(() => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;

    const handleFocus = () => {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
    };

    editor.root.addEventListener('focus', handleFocus);
    return () => editor.root.removeEventListener('focus', handleFocus);
  }, []);

  // Quill modules - simplified toolbar on mobile
  const modules = {
    toolbar: isMobile
      ? [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }]
        ]
      : [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['link'],
          ['clean']
        ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'indent',
    'link'
  ];

  // Extract plain text from HTML
  const extractPlainText = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  // Sanitize HTML to prevent XSS
  const sanitizeHtml = (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3',
        'ol', 'ul', 'li', 'a'
      ],
      ALLOWED_ATTR: ['href', 'target']
    });
  };

  // Manual save function
  const handleSave = useCallback(async () => {
    setIsSaving(true);

    try {
      // Allow saving empty content (user may want to clear notes)
      const contentToSave = value || '';
      const sanitizedHtml = sanitizeHtml(contentToSave);
      const plainText = extractPlainText(sanitizedHtml);

      await onSave(sanitizedHtml, plainText);

      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  }, [value, onSave]);

  // Handle content change
  const handleChange = useCallback((content: string) => {
    setValue(content);
    setHasUnsavedChanges(true);
  }, []);

  // Format last saved time
  const getLastSavedText = (): string => {
    if (!lastSaved) return '';

    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - lastSaved.getTime()) / 1000);

    if (diffSeconds < 60) {
      return 'Saved just now';
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60);
      return `Saved ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      return `Saved at ${lastSaved.toLocaleTimeString()}`;
    }
  };

  const effectiveMinHeight = isMobile ? '100px' : minHeight;

  return (
    <div className="relative" ref={containerRef}>
      {/* Editor Container */}
      <div
        className="border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all"
        style={{ minHeight: effectiveMinHeight }}
      >
        <ReactQuill
          ref={editorRef}
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          theme="snow"
          className="rich-text-editor"
        />
      </div>

      {/* Save Button and Status */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {isSaving ? (
            <>
              <ClockIcon className="w-4 h-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : lastSaved ? (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
              >
                <CheckCircleIcon className="w-4 h-4 text-green-500" />
              </motion.div>
              <span className="text-green-600">{getLastSavedText()}</span>
            </>
          ) : null}
        </div>

        <motion.button
          onClick={handleSave}
          disabled={isSaving || !hasUnsavedChanges}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all min-h-[44px] ${
            hasUnsavedChanges && !isSaving
              ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm hover:shadow-md'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          whileHover={hasUnsavedChanges && !isSaving ? { scale: 1.02 } : {}}
          whileTap={hasUnsavedChanges && !isSaving ? { scale: 0.98 } : {}}
        >
          {isSaving ? 'Saving...' : 'Save Notes'}
        </motion.button>
      </div>

      {/* Custom Styles for Quill */}
      <style>{`
        .rich-text-editor .ql-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          line-height: 1.6;
        }

        .rich-text-editor .ql-editor {
          min-height: ${effectiveMinHeight};
          padding: 16px;
        }

        @media (max-width: 767px) {
          .rich-text-editor .ql-editor {
            min-height: 100px;
            padding: 12px;
          }
        }

        .rich-text-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }

        .rich-text-editor .ql-toolbar {
          background-color: #f9fafb;
          border: none;
          border-bottom: 1px solid #e5e7eb;
          padding: 8px;
        }

        @media (max-width: 767px) {
          .rich-text-editor .ql-toolbar {
            padding: 4px 6px;
          }
        }

        .rich-text-editor .ql-stroke {
          stroke: #6b7280;
        }

        .rich-text-editor .ql-fill {
          fill: #6b7280;
        }

        .rich-text-editor .ql-picker-label {
          color: #6b7280;
        }

        .rich-text-editor .ql-toolbar button:hover,
        .rich-text-editor .ql-toolbar button.ql-active {
          color: #3b82f6;
        }

        .rich-text-editor .ql-toolbar button:hover .ql-stroke,
        .rich-text-editor .ql-toolbar button.ql-active .ql-stroke {
          stroke: #3b82f6;
        }

        .rich-text-editor .ql-toolbar button:hover .ql-fill,
        .rich-text-editor .ql-toolbar button.ql-active .ql-fill {
          fill: #3b82f6;
        }

        .rich-text-editor .ql-editor h1,
        .rich-text-editor .ql-editor h2,
        .rich-text-editor .ql-editor h3 {
          font-weight: 600;
          margin-top: 1em;
          margin-bottom: 0.5em;
        }

        .rich-text-editor .ql-editor h1 {
          font-size: 1.5em;
        }

        .rich-text-editor .ql-editor h2 {
          font-size: 1.25em;
        }

        .rich-text-editor .ql-editor h3 {
          font-size: 1.1em;
        }

        .rich-text-editor .ql-editor ul,
        .rich-text-editor .ql-editor ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }

        .rich-text-editor .ql-editor li {
          margin-bottom: 0.25em;
        }

        .rich-text-editor .ql-editor a {
          color: #3b82f6;
          text-decoration: underline;
        }

        .rich-text-editor .ql-editor strong {
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};
